import type { ApiClient, ApiRequestInit } from './api-client';
import { errorStatus } from './api-error';
import { CSRF_COOKIE, CSRF_HEADER, readCookie } from './cookies';

/**
 * API calls as the logged-in user, on top of an `ApiClient`. Ported from portal-ui-lib's
 * `authInterceptor`, `orgScopeInterceptor` and `errorInterceptor` (401 handling only).
 *
 * - Credentials: the session lives in httpOnly cookies (`tt_access`, `tt_refresh`) set by
 *   `/auth/login`; every call sends them.
 * - CSRF: mutating calls echo the `tt_csrf` cookie in `x-csrf` (the backend's double submit). The
 *   cookie is read per attempt — a refresh replaces it.
 * - Org scope: the selected org node goes out as `?orgNodeId=` and a GLOBAL admin's `scoped`
 *   view mode as `?viewMode=scoped`. This is a *request to narrow*: the backend resolves the
 *   caller's real reach from the session and refuses anything outside it (backend
 *   src/authz/scope.ts). It replaced the `X-Filter-Envelope` header, which no longer exists.
 *   A value the caller set explicitly always wins.
 * - Expired session: a 401 outside `/auth/` triggers one `POST /auth/refresh-token`, shared by
 *   every request that failed meanwhile, then the request is retried once. If the refresh fails,
 *   `onSessionExpired` runs and the original 401 is thrown.
 */

export interface SessionScope {
  orgNodeId: string | null;
  viewMode: 'admin' | 'scoped';
}

export interface SessionClientOptions {
  /** The scope requests are narrowed to, read per request. Omit for a client that never narrows. */
  scope?: () => SessionScope | null;
  /** The session is gone (the refresh token was refused). Called once per failed refresh. */
  onSessionExpired?: () => void;
}

export interface SessionClient {
  /** JSON call as the logged-in user; same contract as `ApiClient.json`. */
  json<T = unknown>(path: string, init?: ApiRequestInit): Promise<T>;
  blob(path: string, init?: ApiRequestInit): Promise<Blob>;
  /** As `ApiClient.withHeaders`, as the logged-in user. */
  withHeaders<T = unknown>(
    path: string,
    init?: ApiRequestInit,
  ): Promise<{ body: T; headers: Headers }>;
  /** Renew the access token from the refresh cookie. Concurrent callers share one attempt. */
  refresh(): Promise<boolean>;
}

/** Paths that are never narrowed to an org node (portal-ui-lib's `SKIP_PATTERNS`). */
const UNSCOPED_PATTERNS = [
  '/auth/',
  '/health',
  '/cors-test',
  '/whoami',
  '/me/access',
  '/org-nodes/tree',
  '/public/',
];

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export function isScopedPath(path: string): boolean {
  return !UNSCOPED_PATTERNS.some(pattern => path.includes(pattern));
}

function isAbsent(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

export function createSessionClient(
  api: ApiClient,
  options: SessionClientOptions = {},
): SessionClient {
  function withSession(path: string, init: ApiRequestInit): ApiRequestInit {
    const headers = new Headers(init.headers);
    // The backend's activity log tells browser traffic from API clients by this header.
    headers.set('X-Requested-With', 'XMLHttpRequest');
    const csrf = readCookie(CSRF_COOKIE);
    if (MUTATING.has((init.method ?? 'GET').toUpperCase()) && csrf) {
      headers.set(CSRF_HEADER, csrf);
    }

    let query = init.query;
    const scope = isScopedPath(path) ? options.scope?.() : null;
    if (scope) {
      query = { ...query };
      if (scope.orgNodeId && isAbsent(query['orgNodeId'])) {
        query['orgNodeId'] = scope.orgNodeId;
      }
      // Only when the admin chose it; never implied by the selected node.
      if (scope.viewMode === 'scoped' && isAbsent(query['viewMode'])) {
        query['viewMode'] = 'scoped';
      }
    }

    return { ...init, headers, query, credentials: 'include' };
  }

  let refreshing: Promise<boolean> | null = null;

  function refresh(): Promise<boolean> {
    refreshing ??= api
      .json('/auth/refresh-token', withSession('/auth/refresh-token', { method: 'POST' }))
      .then(
        () => true,
        () => {
          options.onSessionExpired?.();
          return false;
        },
      )
      .finally(() => {
        refreshing = null;
      });
    return refreshing;
  }

  async function call<T>(
    send: (path: string, init: ApiRequestInit) => Promise<T>,
    path: string,
    init: ApiRequestInit = {},
  ): Promise<T> {
    try {
      return await send(path, withSession(path, init));
    } catch (error) {
      const renewable = errorStatus(error) === 401 && !path.startsWith('/auth/');
      if (renewable && (await refresh())) {
        return send(path, withSession(path, init));
      }
      throw error;
    }
  }

  return {
    json: <T>(path: string, init?: ApiRequestInit) => call<T>(api.json, path, init),
    blob: (path: string, init?: ApiRequestInit) => call(api.blob, path, init),
    withHeaders: <T>(path: string, init?: ApiRequestInit) =>
      call<{ body: T; headers: Headers }>(api.withHeaders, path, init),
    refresh,
  };
}
